import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '../../../environments/environment';
import { OidcSecurityService, UserDataResult } from 'angular-auth-oidc-client';
import { filter, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { UserService } from './user.service';
import { firstValueFrom } from 'rxjs';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private currentUser: any;
  private isAuth = false;
  private userId: number | null = null;
  private ensureUserIdPromise: Promise<number> | null = null;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private oidc: OidcSecurityService,
    private router: Router,
    private http: HttpClient,
    private userService: UserService
  ) {
    if (isPlatformBrowser(this.platformId)) {
      const userData = localStorage.getItem('currentUser');
      if (userData) {
        this.currentUser = JSON.parse(userData);
      }
      // Cargar userId persistido
      const storedId = sessionStorage.getItem('userId') || localStorage.getItem('userId');
      if (storedId) {
        const idNum = Number(storedId);
        this.userId = Number.isFinite(idNum) ? idNum : null;
      }
    }
    if (!environment.useDummyLogin && isPlatformBrowser(this.platformId)) {
      this.oidc.isAuthenticated$.subscribe(({ isAuthenticated }) => {
        this.isAuth = isAuthenticated;
      });

      this.oidc.isAuthenticated$
        .pipe(filter(r => r.isAuthenticated), take(1))
        .subscribe(() => {
          this.oidc.userData$
            .pipe(
              filter(u => !!(u as any)?.userData && Object.keys((u as any).userData).length > 0),
              take(1)
            )
            .subscribe(u => {
              const idClaims = (u as any).userData;
              console.log('[OIDC] idClaims:', idClaims);
              this.currentUser = { ...(this.currentUser || {}), ...idClaims };
              localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
              this.logCurrent();
              // Intentar obtener userId cuando tengamos los claims
              this.ensureUserId().catch(() => {
                // Silencioso aquí; el callback/UX manejará errores de navegación
              });
            });
          // const at = (this.oidc as any).getAccessToken?.() ?? '';

          // if (typeof at === 'string' && at) {
          //   this.setRolesFromAccessToken(at);
          // } else {
          //   (at as any)?.pipe(take(1)).subscribe((tok: string) => this.setRolesFromAccessToken(tok));
          // }


        });

    }

  }

  login(username?: string, password?: string): boolean {
    if (environment.useDummyLogin) {
      if (username === 'novedades@oxy.cl' && password === 'admin') {
        this.currentUser = { username, roles: ['novedades'] };
      } else if (username === 'cde@oxy.cl' && password === 'admin') {
        this.currentUser = { username, roles: ['cde'] };
      } else {
        return false;
      }
      if (isPlatformBrowser(this.platformId)) {
        localStorage.setItem('currentUser', JSON.stringify(this.currentUser));
      }

      // Redirigir según el perfil después del login dummy
      setTimeout(() => {
        this.routeAfterLogin();
      }, 100);

      return true;
    } else {
      this.oidc.authorize();
      return true;
    }
  }

  logout(): void {
    if (environment.useDummyLogin) {
      this.currentUser = null;
      if (isPlatformBrowser(this.platformId)) {
        localStorage.removeItem('currentUser');
      }
      this.isAuth = false;
      this.clearUserId();
    } else {
      this.clearUserId();
      this.oidc.logoff();
    }
  }

  isAuthenticated(): boolean {
    return environment.useDummyLogin ? !!this.currentUser : this.isAuth;
  }

  hasRole(role: string): boolean {
    if (!this.currentUser) {
      return false;
    }
    if (environment.useDummyLogin) {
      return this.currentUser.roles?.includes(role) ?? false;
    } else {
      const roles: string[] = this.currentUser['cognito:groups'] || this.currentUser['roles'] || [];
      return Array.isArray(roles) && roles.includes(role);
    }
  }

  getCurrentUser(): any {
    return this.currentUser;
  }

  /** Obtiene el userId persistido (sessionStorage prioritario) */
  getUserId(): number | null {
    if (this.userId != null) return this.userId;
    if (isPlatformBrowser(this.platformId)) {
      const s = sessionStorage.getItem('userId') || localStorage.getItem('userId');
      if (s) {
        const n = Number(s);
        this.userId = Number.isFinite(n) ? n : null;
        return this.userId;
      }
    }
    return null;
  }

  /** Persiste el userId y lo mantiene en memoria */
  private setUserId(id: number, persist: 'session' | 'local' = 'session') {
    this.userId = id;
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      if (persist === 'session') {
        sessionStorage.setItem('userId', String(id));
      } else {
        localStorage.setItem('userId', String(id));
      }
    } catch { }
  }

  /** Limpia el userId de todos los almacenamientos */
  private clearUserId() {
    this.userId = null;
    if (!isPlatformBrowser(this.platformId)) return;
    try { sessionStorage.removeItem('userId'); } catch { }
    try { localStorage.removeItem('userId'); } catch { }
  }

  /** Extrae el username desde los claims conocidos */
  private extractUsernameFromClaims(u: any): string | null {
    if (!u) return null;
    return (
      u['cognito:username'] || u['preferred_username'] || u['username'] || u['email'] || null
    );
  }

  /**
   * Asegura que exista userId. Si no existe, llama POST /User/access con el username de Cognito.
   * Retorna una promesa que resuelve con el userId o rechaza con el error.
   */
  // async ensureUserId(): Promise<number> {
  //   const existing = this.getUserId();
  //   if (existing != null) return Promise.resolve(existing);

  //   // Si ya hay una llamada en curso, reutilizarla para evitar duplicados
  //   if (this.ensureUserIdPromise) return this.ensureUserIdPromise;

  //   const username = this.extractUsernameFromClaims(this.currentUser);
  //   if (!username) {
  //     console.error('[Auth] No se pudo determinar username desde claims');
  //     return Promise.reject('No se pudo determinar el username');
  //   }

  //   const url = `${environment.apiUrl}/User/access`;
  //   //const body = { username } as any;
  //   //ojo
  //   const body = {
  //     username: this.currentUser.username,
  //     fullName: this.currentUser.name,
  //     email: this.currentUser.email
  //   }
  //   this.ensureUserIdPromise = this.http.post<{ userId: number; created: boolean }>(url, body).toPromise()
  //     .then(res => {
  //       if (res && typeof res.userId === 'number') {
  //         this.setUserId(res.userId, 'session');
  //         console.log('[Auth] userId obtenido y persistido:', res);
  //          const at = (this.oidc as any).getAccessToken?.() ?? '';
  //         const user = await this.getUserById(res.userId)
  //         if (typeof at === 'string' && at) {
  //           this.setRolesFromAccessToken(at, user!);
  //         } else {
  //           (at as any)?.pipe(take(1)).subscribe((tok: string) => this.setRolesFromAccessToken(tok, user!));
  //         }
  //         return res.userId;
  //       }
  //       throw new Error('Respuesta inválida obteniendo userId');
  //     })
  //     .catch(err => {
  //       console.error('[Auth] Error en POST /User/access:', err);
  //       throw err;
  //     })
  //     .finally(() => {
  //       // Liberar la promesa en curso; en adelante getUserId() devolverá valor y evitará repetir POST
  //       this.ensureUserIdPromise = null;
  //     });

  //   return this.ensureUserIdPromise;
  // }

  async ensureUserId(): Promise<number> {
    const existingId = this.getUserId();
    if (existingId != null) return existingId;

    if (this.ensureUserIdPromise) return this.ensureUserIdPromise;

    const { username, name: fullName, email } = this.currentUser;

    if (!username) {
      console.error('[Auth] No se pudo determinar username desde claims');
      throw new Error('No se pudo determinar el username');
    }

    const url = `${environment.apiUrl}/User/access`;
    const body = { username, fullName, email };

    this.ensureUserIdPromise = this.processUserAccess(url, body);

    try {
      return await this.ensureUserIdPromise;
    } finally {
      this.ensureUserIdPromise = null;
    }
  }

  private async processUserAccess(url: string, body: any): Promise<number> {
    try {
      const res = await this.http.post<{ userId: number; created: boolean }>(url, body).toPromise();

      if (!res || typeof res.userId !== 'number') {
        throw new Error('Respuesta inválida obteniendo userId');
      }

      this.setUserId(res.userId, 'session');

      const [user, accessToken] = await Promise.all([
        this.getUserById(res.userId),
        Promise.resolve((this.oidc as any).getAccessToken?.() ?? '')
      ]);

      this.handleTokenRoles(accessToken, user);

      return res.userId;
    } catch (err) {
      console.error('[Auth] Error en POST /User/access:', err);
      throw err;
    }
  }

  private handleTokenRoles(at: any, user: any): void {
    if (typeof at === 'string' && at) {
      this.setRolesFromAccessToken(at, user);
    } else if (at?.pipe) {
      at.pipe(take(1)).subscribe((tok: string) => this.setRolesFromAccessToken(tok, user));
    }
  }

  /**
   * Verifica si el usuario puede acceder a una funcionalidad específica basada en grupos de Cognito
   */
  canAccess(feature: string): boolean {

    // console.log('canAccess, feature', feature)
    // console.log('canAccess, currentUser', this.currentUser)
    if (!this.currentUser) {
      return false;
    }

    // Buscar roles en ambas propiedades por compatibilidad
    const groups: string[] = this.currentUser['cognito:groups'] || this.currentUser['roles'] || [];
    // console.log('canAccess, groups', groups)

    // Si tiene el rol 'novedades', puede ver todo (retrocompatibilidad)
    if (groups.includes('novedades')) {
      return true;
    }

    // Si tiene el rol 'cde', puede ver todo el módulo CDE
    if (groups.includes('cde')) {
      return feature.startsWith('cde');
    }

    // Reglas específicas para nuevos grupos
    switch (feature) {
      // DCS Operator - solo para operadores DCS y usuarios completos
      case 'dcs':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('novedades');

      // Informes y turnos históricos - para ambos operadores
      case 'shift-report':
      case 'historical-shift':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('Novedades_Operador_Ferrico') ||
          groups.includes('Novedades_Salmuera_I') ||
          groups.includes('Novedades_Salmuera_II') ||
          groups.includes('Novedades_Compresion') ||
          groups.includes('Novedades_Electrolizadores') ||
          groups.includes('Novedades_Subestacion') ||
          groups.includes('Novedades_Movimiento_Producto') ||
          groups.includes('novedades');

      // Cloruro Férrico - solo para operadores de férrico
      case 'cloruro-ferrico':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('Novedades_Operador_Ferrico') ||
          groups.includes('novedades');

      // Otras secciones de novedades - solo para usuarios con acceso completo
      case 'salmuera-i':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('Novedades_Salmuera_I') ||
          groups.includes('novedades');
      case 'salmuera-ii':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('Novedades_Salmuera_II') ||
          groups.includes('novedades');
      case 'comprension':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('Novedades_Compresion') ||
          groups.includes('novedades');
      case 'electrolizadores':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('Novedades_Electrolizadores') ||
          groups.includes('novedades');
      case 'subestacion':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('Novedades_Subestacion') ||
          groups.includes('novedades');
      case 'mov-prod':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('Novedades_Movimiento_Producto') ||
          groups.includes('novedades');
      case 'mis-novedades':
      case 'daily-shift':
        return groups.includes('novedades');

      // Botón iniciar turno - disponible para todos los grupos de novedades
      case 'iniciar-turno':
        return groups.includes('Novedades_Operador_DCS') ||
          groups.includes('Novedades_Operador_Ferrico') ||
          groups.includes('Novedades_Salmuera_I') ||
          groups.includes('Novedades_Salmuera_II') ||
          groups.includes('Novedades_Compresion') ||
          groups.includes('Novedades_Electrolizadores') ||
          groups.includes('Novedades_Subestacion') ||
          groups.includes('Novedades_Movimiento_Producto') ||
          groups.includes('novedades');

      // Funcionalidades CDE
      case 'cde-dashboard':
      case 'cde-charts':
      case 'cde-quick-actions':
      case 'cde-container':
      case 'cde-mantenedores':
      case 'cde-notificaciones':
        return groups.includes('cde');

      default:
        return false;
    }
  }

  /**
   * Obtiene el perfil del usuario basado en sus grupos
   */
  getUserProfile(): string {
    if (!this.currentUser) {
      return 'Sin perfil';
    }

    const groups: string[] = this.currentUser['cognito:groups'] || this.currentUser['roles'] || [];

    if (groups.includes('novedades')) {
      return 'Novedades - Acceso Completo';
    } else if (groups.includes('Novedades_Operador_DCS')) {
      return 'Operador DCS';
    } else if (groups.includes('Novedades_Operador_Ferrico')) {
      return 'Operador Cloruro Férrico';
    } else if (groups.includes('Novedades_Salmuera_II')) {
      return 'Operador Salmuera II';
    } else if (groups.includes('Novedades_Salmuera_I')) {
      return 'Operador Salmuera I';
    } else if (groups.includes('Novedades_Compresion')) {
      return 'Operador Compresión';
    } else if (groups.includes('Novedades_Electrolizadores')) {
      return 'Operador Electrolizadores';
    } else if (groups.includes('Novedades_Subestacion')) {
      return 'Operador Subestación';
    } else if (groups.includes('Novedades_Movimiento_Producto')) {
      return 'Operador Movimiento de Productos';
    } else if (groups.includes('cde')) {
      return 'CDE - Acceso Completo';
    } else {
      return 'Perfil no definido';
    }
  }

  /**
   * Redirige al usuario según su perfil después del login
   */
  private routeAfterLogin(): void {
    if (!this.currentUser) {
      console.log('[routeAfterLogin] No hay currentUser, saliendo');
      return;
    }

    const groups: string[] = this.currentUser['cognito:groups'] || this.currentUser['roles'] || [];
    console.log('[routeAfterLogin] Grupos del usuario:', groups);

    let targetRoute = '/dashboards/home'; // Default route

    if (groups.includes('novedades')) {
      targetRoute = '/mis-novedades';
      console.log('[routeAfterLogin] Usuario tiene grupo "novedades", redirigiendo a /mis-novedades');
    } else if (groups.includes('Novedades_Operador_DCS')) {
      targetRoute = '/dcs';
      console.log('[routeAfterLogin] Usuario tiene grupo "Novedades_Operador_DCS", redirigiendo a /dcs');
    } else if (groups.includes('Novedades_Operador_Ferrico')) {
      targetRoute = '/cloruro-ferrico';
      console.log('[routeAfterLogin] Usuario tiene grupo "Novedades_Operador_Ferrico", redirigiendo a /cloruro-ferrico');
    } else if (groups.includes('Novedades_Salmuera_II')) {
      targetRoute = '/salmuera-ii';
      console.log('[routeAfterLogin] Usuario tiene grupo "Novedades_Salmuera_II", redirigiendo a /salmuera-ii');
    } else if (groups.includes('Novedades_Salmuera_I')) {
      targetRoute = '/salmuera-i';
      console.log('[routeAfterLogin] Usuario tiene grupo "Novedades_Salmuera_I", redirigiendo a /salmuera-i');
    } else if (groups.includes('Novedades_Compresion')) {
      targetRoute = '/comprension';
      console.log('[routeAfterLogin] Usuario tiene grupo "Novedades_Compresion", redirigiendo a /comprension');
    } else if (groups.includes('Novedades_Electrolizadores')) {
      targetRoute = '/electrolizadores';
      console.log('[routeAfterLogin] Usuario tiene grupo "Novedades_Electrolizadores", redirigiendo a /electrolizadores');
    } else if (groups.includes('Novedades_Subestacion')) {
      targetRoute = '/subestacion';
      console.log('[routeAfterLogin] Usuario tiene grupo "Novedades_Subestacion", redirigiendo a /subestacion');
    } else if (groups.includes('Novedades_Movimiento_Producto')) {
      targetRoute = '/mov-prod';
      console.log('[routeAfterLogin] Usuario tiene grupo "Novedades_Movimiento_Producto", redirigiendo a /mov-prod');
    } else if (groups.includes('cde')) {
      targetRoute = '/dashboards/home';
      console.log('[routeAfterLogin] Usuario tiene grupo "cde", redirigiendo a /dashboards/home');
    } else {
      console.log('[routeAfterLogin] Usuario no tiene grupos reconocidos, redirigiendo por defecto a /dashboards/home');
    }

    console.log('[routeAfterLogin] Navegando a:', targetRoute);
    console.log('[routeAfterLogin] URL actual antes de navegar:', this.router.url);

    this.router.navigateByUrl(targetRoute).then(success => {
      console.log('[routeAfterLogin] Navegación exitosa:', success);
      console.log('[routeAfterLogin] URL actual después de navegar:', this.router.url);
    }).catch(error => {
      console.error('[routeAfterLogin] Error en navegación:', error);
    });
  }



  private decodeJwt(token: string): any {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = atob(padded);
    return JSON.parse(json);
  }

  private async setRolesFromAccessToken(token: string, user: User) {
    if (!token) return;
    try {
      const claims = this.decodeJwt(token) || {};
      const roles = Array.isArray(claims['cognito:groups']) ? claims['cognito:groups'] : [];
      console.log('[setRolesFromAccessToken] Roles del token:', roles);

      //const user: User | null = await this.getUserById(userId)
      //aca
      type relacionDB = Record<string, string>;
      const diccionario: relacionDB =
      {
        "general": "novedades",
        "dcs": "Novedades_Operador_DCS",
        "cloruro_ferrico": "Novedades_Operador_Ferrico",
        "salmuera_i": "Novedades_Salmuera_I",
        "salmuera_ii": "Novedades_Salmuera_II",
        "compresion": "Novedades_Compresion",
        "electrolizadores": "Novedades_Electrolizadores",
        "subestacion": "Novedades_Subestacion",
        "movimiento_producto": "Novedades_Movimiento_Producto"
      }
      //buscar a que area pertenece el ususario de la DB nuestra
      //si general -> novedades
      // dcs -> Novedades_Operador_DCS
      let rolesTransformados = [diccionario[user?.area?.code!]]//roles.map(item => diccionario[item] ?? item);
      if(user.area == null) rolesTransformados = ['cde']
      this.currentUser = { ...(this.currentUser || {}), roles: rolesTransformados };
      localStorage.setItem('currentUser', JSON.stringify(this.currentUser));

      console.log('[setRolesFromAccessToken] currentUser después de actualizar:', this.currentUser);
      this.logCurrent();

      // Redirigir según el perfil del usuario después de la autenticación
      // Nota: Removemos la navegación automática aquí para evitar conflictos con guards
      // El callback component manejará la navegación después de un delay apropiado
      console.log('[setRolesFromAccessToken] Usuario autenticado, roles procesados. Navegación será manejada por callback component.');

    } catch (e) {
      console.warn('No se pudo decodificar el access token:', e);
    }
  }

  private logCurrent() {
    console.log('[Auth] currentUser completo:', this.currentUser);
    console.log('[Auth] currentUser.roles:', this.currentUser?.['roles']);
    console.log('[Auth] currentUser["cognito:groups"]:', this.currentUser?.['cognito:groups']);

    const groups = this.currentUser?.['cognito:groups'] || this.currentUser?.['roles'] || [];
    console.log('[Auth] user groups (combinados):', groups);
    console.log('[Auth] user profile:', this.getUserProfile());
    console.log('[Auth] can access DCS?', this.canAccess('dcs'));
    console.log('[Auth] can access iniciar-turno?', this.canAccess('iniciar-turno'));

  }


  async getUserById(id: number): Promise<User | null> {
    try {
      const res = await firstValueFrom(this.userService.getById(this.userId!))
      return res
    } catch {
      return null
    }
  }


  


}
